import { useCallback } from "react";
import { useAppSelector } from "@/core/store";
import { useCartSyncContext } from "@/context/CartSyncContext";
import { selectCartItems, selectCartTotal } from "@/core/orders/selectors";
import { selectSource } from "@/core/app/selectors";
import { orderToOrderData } from "@/core/orders/map";
import { sendIframeMessage } from "@/core/orders/utils";
import type { ErrorData } from "@/types/iframe-messages";

/**
 * Нагрузка PREORDER_ERROR. Переехала из core/orders/utils вместе с
 * отправителем: прежде её собирала сага, теперь — кнопка.
 */
function errorDataForIframe(
  error: string,
  cartItems: number,
  cartSum: number
): ErrorData {
  return {
    error,
    code: "ORDER_CREATION_FAILED",
    details: { timestamp: Date.now(), cartItems, cartSum },
  };
}

export interface CompletePreorder {
  /** Отдать заказ хосту. true — сообщение ушло, false — отправлять было нечего либо ошибка. */
  complete: () => Promise<boolean>;
}

/**
 * Кнопка предзаказа: «я закончил, вот идентификатор черновика».
 *
 * Заказ к этому моменту уже существует — его завела фоновая синхронизация,
 * поэтому кнопка ничего не создаёт. Она флашит незавершённый дебаунс (иначе
 * хост получил бы состав без последней правки), дожидается ответа и отдаёт
 * наружу OrderData.
 *
 * Гость не «оформляет» заказ: черновик остаётся черновиком, снимает флаг
 * вебхук Restoplace. PREORDER_COMPLETED значит «черновик готов», а не
 * «заказ оформлен».
 */
export function useCompletePreorder(): CompletePreorder {
  const { flush } = useCartSyncContext();
  const source = useAppSelector(selectSource);
  const cartItems = useAppSelector(selectCartItems);
  const cartSum = useAppSelector(selectCartTotal);

  const complete = useCallback(async (): Promise<boolean> => {
    if (cartItems.length === 0) return false;

    try {
      const order = await flush();
      if (!order) return false;

      sendIframeMessage("PREORDER_COMPLETED", source, orderToOrderData(order));
      return true;
    } catch (error) {
      sendIframeMessage(
        "PREORDER_ERROR",
        source,
        errorDataForIframe(
          error instanceof Error ? error.message : "Не удалось отправить заказ",
          cartItems.length,
          cartSum
        )
      );
      return false;
    }
  }, [flush, source, cartItems, cartSum]);

  return { complete };
}

export default useCompletePreorder;
